import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Lock, AlertCircle } from 'lucide-react';
import SecurePDFViewer from '@/components/SecurePDFViewer';
import { API } from '@/App';

const SecureArticleViewerPage = () => {
  const { articleId } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [user, setUser] = useState(null);

  useEffect(() => {
    fetchArticleAndUser();
  }, [articleId]);

  const fetchArticleAndUser = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/auth');
        return;
      }

      // Get current user
      const userResponse = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(userResponse.data);

      // Get article
      const articleResponse = await axios.get(`${API}/articles/${articleId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const articleData = articleResponse.data;
      
      // Check if user has access
      const isAdmin = userResponse.data.role === 'super_admin' || userResponse.data.role === 'regional';
      if (articleData.is_premium && !isAdmin && userResponse.data.subscription_status !== 'active') {
        setError('Vous devez avoir un abonnement actif pour accéder à ce contenu premium');
        setLoading(false);
        return;
      }

      setArticle(articleData);
    } catch (err) {
      console.error('Error fetching article:', err);
      if (err.response?.status === 401) {
        navigate('/auth');
      } else if (err.response?.status === 404) {
        setError('Article non trouvé');
      } else {
        setError('Erreur lors du chargement de l\'article');
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-cyan-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement du document sécurisé...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Accès refusé</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <Button onClick={() => navigate('/articles')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour aux articles
          </Button>
        </div>
      </div>
    );
  }

  if (!article || !user) {
    return null;
  }

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header */}
      <div className="bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/articles')}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Retour
            </Button>
            <div>
              <h1 className="text-xl font-bold text-gray-900">{article.title}</h1>
              {article.is_premium && (
                <div className="flex items-center gap-2 mt-1">
                  <Lock className="w-4 h-4 text-yellow-600" />
                  <span className="text-sm text-yellow-600 font-medium">Article Premium</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* PDF Viewer (lecture seule) */}
      <div className="flex-1">
        {article.pdf_url ? (
          <SecurePDFViewer
            pdfUrl={article.pdf_url}
            userName={`${user.first_name} ${user.last_name}`}
            userEmail={user.email}
          />
        ) : article.content ? (
          <div className="p-8">
            <div className="prose max-w-none text-gray-700 leading-relaxed" dangerouslySetInnerHTML={{ __html: article.content }} />
          </div>
        ) : (
          <div className="flex items-center justify-center h-full bg-gray-100">
            <div className="text-center">
              <AlertCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 text-lg">Contenu disponible en ligne uniquement</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SecureArticleViewerPage;
